import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog } from '@material-ui/core';
import moment from 'utils/moment';
import SectionHeader from '../../../components/sectionHeader';
import icon from '../../../icons/GateModule.png';
import styles from './GateAllocations.module.scss';
import { fetchGateAllocationData, fetchGateRequestLogs, getGateList } from '../redux/actions';
import ModalGate from './ModalGate';
import RequestDetail from './RequestDetailModal';
import AllocationScheduler from '../Scheduler/AllocationScheduler';

const currentDate = moment(new Date()).startOf('day').toISOString();

const GateAllocation = () => {
  const dispatch = useDispatch();
  const allocationList = useSelector(state => state?.gate?.allocationData?.results);
  const gateRequests = useSelector(state => state?.gate?.gateRequests?.results);
  const gateList = useSelector(state => state?.gate?.gateList);
  const [gates, setGates] = useState([]);
  const [data, setData] = useState([]);
  const [gateUpdateList, setGateUpdateList] = useState([]);
  const [selectedItem, setSelectedItem] = useState({
    show: false,
    data: {}
  });

  const sortedData = (sorted_data = []) => sorted_data.sort(({ text: a }, { text: b }) => {
      const isUpperA = a[0] === a[0].toUpperCase();
      const isUpperB = b[0] === b[0].toUpperCase();

      if (isUpperA !== isUpperB) {
          return isUpperA ? -1 : 1;
      }

      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  });

  useEffect(() => {
    const start = moment().format('YYYY-MM-DD');
    dispatch(getGateList());
    dispatch(fetchGateRequestLogs());
    dispatch(fetchGateAllocationData(start, start, true));
  }, [dispatch]);

  useEffect(() => {
    if (
      Array.isArray(gateList)
      && Array.isArray(allocationList)
      && gateList.length
      && allocationList.length
    ) {
      const gatesData = gateList
        .filter(item => item?.rule?.show_allocation)
        .map(item => ({
          id: item.gate_number,
          text: item.gate_number,
          label: item.gate_number
        }));

      setGates(sortedData(gatesData));
      setData(allocationList);
    }
  }, [allocationList, gateList]);

  useEffect(() => {
    const statusColors = {
      'approve': '#409000',
      'Approve': '#409000',
      'Pending': '#e6bb3f',
      'pending': '#e6bb3f'
    };
    if (gateRequests?.length) {
      const gate_updatesList = gateRequests?.filter(item => item?.to_gate)
      .map(item => ({
        ...item,
        to_gateId: item.to_gate,
        gateId: item.to_gate,
        dateStart: item.in_date,
        dateEnd: item.from_date,
        displayText: item.flight_number,
        label: item.flight_number,
        backgroundColor: statusColors[item?.status],
        displayStart: moment(item.in_date).format('h:mm A'),
        displayEnd: moment(item.from_date).format('h:mm A'),
        flightId: true,
        airline: item?.airline?.name
      }));
      setGateUpdateList(gate_updatesList);
    }
  }, [gateRequests]);

  const assignmentData = data?.map((item) => {
    const flightDuration = 60;

    const departure = item?.actual_departure
      ? moment(item?.actual_departure, 'HH:mm').format().slice(0, -6)
      : item?.departure_timestamp
        && moment(item?.departure_timestamp).format().slice(0, -6);

    const arrival = item?.actual_arrival
      ? moment(item?.actual_arrival, 'HH:mm').format().slice(0, -6)
      : item?.arrival_timestamp
        && moment(item?.arrival_timestamp).format().slice(0, -6);

    const departureTurn = item?.actual_arrival
      ? moment(item?.actual_arrival, 'HH:mm').add(flightDuration, 'm').format().slice(0, -6)
      : item?.arrival_timestamp
        && moment(item?.arrival_timestamp).add(flightDuration, 'm').format().slice(0, -6);

    const arrivalTurn = item?.actual_departure
      ? moment(item?.actual_departure, 'HH:mm').subtract(flightDuration, 'm').format().slice(0, -6)
      : item?.departure_timestamp
        && moment(item?.departure_timestamp).subtract(flightDuration, 'm').format().slice(0, -6);

    const endDate = departure || departureTurn;
    const startDate = arrival || arrivalTurn;

    const flight_number = (
      item?.arrival_flight_number?.toString() === item?.departure_flight_number?.toString()
        ? item?.arrival_flight_number
        : (
          item?.arrival_flight_number && item?.arrival_flight_number !== null
            ? item?.arrival_flight_number
            : ''
        ) + (
          item?.departure_flight_number && item?.departure_flight_number !== 'None'
            ? `${item?.arrival_flight_number ? '/' : ''}${item?.departure_flight_number}`
            : ''
        )
    );

    return {
      id: item?.id,
      gateId: item.gate_key,
      arrival_flight_number: item.arrival_flight_number,
      departure_flight_number: item.departure_flight_number,
      text: flight_number,
      label: flight_number,
      dateStart: startDate,
      dateEnd: endDate,
      gate: item?.gate_key,
      date: item.date,
      aircraft_type: item.aircraft_type,
      from_city_icao: item.from_city_icao,
      to_city_icao: item.to_city_icao,
      status: item.status,
      backgroundColor: '#77b51f',
      data_arrival: item?.actual_arrival,
      data_departure: item?.actual_departure,
      displayStart: moment(startDate).format('h:mm A'),
      displayEnd: moment(endDate).format('h:mm A'),
      displayText: flight_number,
      urlText: '',
      airline_name: item.airline && item.airline.split(' ')?.[0],
      ...item
    };
  });

  const isTimeConflict = (requestStart, requestEnd, gateStart, gateEnd) => moment(requestStart).isBefore(moment(gateEnd))
    && moment(requestEnd).isAfter(moment(gateStart));
  const conflictArray = (gateUpdateList || []).map((item) => {
    const hasConflict = (assignmentData || []).some(item2 => item?.gateId === item2?.gateId
      && isTimeConflict(item.dateStart, item.dateEnd, item2.dateStart, item2.dateEnd));

    if (hasConflict) {
      return { ...item, backgroundColor: '#bd3928' };
    }

    return item;
  });

  const handleItemClick = (event) => {
    const clickedItem = event?.detail?.itemObj || event;

    setSelectedItem((prev) => {
      const isSameItem = prev.show;
      return isSameItem
        ? { show: false, data: {} }
        : { show: true, data: clickedItem };
    });
  };

  return (
    <>
      <SectionHeader
        icon={icon}
        translationID="topbar.gate_allocation"
        defaultTitle="Gate Allocation"
      />
      <div className={`container allocation ${styles.container}`}
        style={{
          height: 'calc(100dvh - 191px)',
          overflowY: 'auto'
        }}
      >
        <AllocationScheduler
          rawEventsData={[...conflictArray, ...assignmentData]}
          columnsData={gates}
          disableResize
          disableDrag
          currentTimeIndicator
          groupId="gateId"
          dateCurrent={currentDate}
          onItemClick={handleItemClick}
        />
        <Dialog
          scroll="paper"
          maxWidth={false}
          open={selectedItem.show}
          onClose={handleItemClick}
          aria-labelledby="scroll-dialog-title"
          aria-describedby="scroll-dialog-description"
        >
          {
            selectedItem?.data?.flightId ? (
            <RequestDetail allocation modalId={selectedItem?.data?.id} onClose={handleItemClick} gateRequest={selectedItem?.data} />
          )
          : <ModalGate data={selectedItem?.data} onClose={handleItemClick} assignmentData={assignmentData} />
          }
        </Dialog>
      </div>
    </>
  );
};

export default GateAllocation;
